import React, { useEffect, useState } from 'react';
import { getStaff, createStaff, updateStaff, deleteStaff, departmentAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { SkeletonTable } from '../components/Skeleton';
import { exportCSV } from '../utils/exportCSV';

const emptyForm = {
  name: '', employeeCode: '', designation: '', phone: '', email: '', joinDate: '',
  dateOfBirth: '',
  basicSalary: '', daPercent: '17', hraPercent: '10', pfPercent: '12',
  status: 'ACTIVE', departmentId: '', department: null,
};

const AVATAR_COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#8b5cf6','#06b6d4','#ec4899','#84cc16'];
function initials(name) { return name?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || '?'; }

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748b', marginBottom: 5, letterSpacing: 0.5, textTransform: 'uppercase' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
  fontSize: 13, boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.15s',
  background: '#fff', color: '#0f172a',
};

export default function Staff() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const handleExport = () => {
    exportCSV('staff-list.csv',
      ['Name','Employee Code','Designation','Department','Phone','Email','Join Date','Basic Salary','Status'],
      staff.map(s => [s.name, s.employeeCode, s.designation || '', s.department?.name || '', s.phone || '', s.email || '', s.joinDate || '', s.basicSalary || '', s.isActive !== false ? 'ACTIVE' : 'INACTIVE'])
    );
  };

  useEffect(() => {
    load();
    departmentAPI.getAll().then(r => setDepartments(r.data || [])).catch(() => {});
  }, []);

  const load = () => {
    setLoading(true);
    getStaff().then(r => { setStaff(r.data || []); setLoading(false); }).catch(() => setLoading(false));
  };

  const openAdd = () => { setEditing(null); setForm(emptyForm); setModal(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({
      name:        s.name        || '',
      employeeCode:s.employeeCode|| '',
      designation: s.designation || '',
      phone:       s.phone       || '',
      email:       s.email       || '',
      joinDate:    s.joinDate    || '',
      basicSalary: s.basicSalary || '',
      daPercent:   s.daPercent   || '17',
      hraPercent:  s.hraPercent  || '10',
      pfPercent:   s.pfPercent   || '12',
      status:      s.isActive !== false ? 'ACTIVE' : 'INACTIVE',
      dateOfBirth: s.dateOfBirth  || '',
      departmentId:s.department?.id?.toString() || '',
      department:  s.department  || null,
    });
    setModal(true);
  };

  const buildPayload = () => ({
    name:          form.name,
    employeeCode:  form.employeeCode,
    designation:   form.designation   || null,
    phone:         form.phone         || null,
    email:         form.email         || null,
    joinDate:      form.joinDate      || null,
    basicSalary:   form.basicSalary   ? Number(form.basicSalary)  : null,
    daPercent:     form.daPercent     ? Number(form.daPercent)    : null,
    hraPercent:    form.hraPercent    ? Number(form.hraPercent)   : null,
    pfPercent:     form.pfPercent     ? Number(form.pfPercent)    : null,
    dateOfBirth:   form.dateOfBirth   || null,
    isActive:      form.status !== 'INACTIVE',
    department:    form.departmentId ? { id: Number(form.departmentId) } : null,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildPayload();
    try {
      if (editing) { await updateStaff(editing.id, payload); toast.success('Staff updated!'); }
      else { await createStaff(payload); toast.success('Staff added!'); }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || err.message || 'Failed to save'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this staff member?')) return;
    try { await deleteStaff(id); toast.success('Deleted!'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const filtered = staff.filter(s =>
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.employeeCode?.toLowerCase().includes(search.toLowerCase()) ||
    s.department?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const active = staff.filter(s => s.isActive === true).length;
  const inactive = staff.filter(s => s.isActive !== true).length;

  return (
    <div>
      {/* KPI Cards */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        {[
          { icon: '👥', label: 'Total Staff',   value: staff.length, color: '#3b82f6', bg: '#eff6ff' },
          { icon: '✅', label: 'Active',          value: active,       color: '#10b981', bg: '#d1fae5' },
          { icon: '⛔', label: 'Inactive',        value: inactive,     color: '#ef4444', bg: '#fee2e2' },
        ].map(k => (
          <div key={k.label} style={{ flex: 1, background: '#fff', borderRadius: 14, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', borderTop: `4px solid ${k.color}`, position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', right: -6, top: -6, width: 54, height: 54, borderRadius: '50%', background: k.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{k.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: '#0f172a' }}>{k.value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, fontWeight: 500 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Search + Actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: 15 }}>🔍</span>
          <input placeholder="Search by name, employee code, department..." value={search}
            onChange={e => setSearch(e.target.value)} style={{
              width: '100%', padding: '11px 14px 11px 40px', borderRadius: 10,
              border: '1.5px solid #e2e8f0', fontSize: 13, background: '#fff',
              boxSizing: 'border-box', outline: 'none',
            }}
            onFocus={e => e.target.style.borderColor = '#3b82f6'}
            onBlur={e => e.target.style.borderColor = '#e2e8f0'}
          />
        </div>
        <button onClick={handleExport} style={{ padding: '10px 18px', background: 'linear-gradient(135deg, #059669, #10b981)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 12px rgba(5,150,105,0.3)', whiteSpace: 'nowrap' }}>⬇ Export CSV</button>
        <button onClick={openAdd} style={{ padding: '10px 22px', background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)', whiteSpace: 'nowrap' }}>+ Add Staff</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #f1f5f9' }}>
              {['Employee', 'Contact', 'Department', 'Salary', 'Status', 'Actions'].map(h => (
                <th key={h} style={{ padding: '13px 20px', textAlign: 'left', fontWeight: 700, color: '#475569', fontSize: 12, letterSpacing: 0.3 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>⏳</div>Loading...
              </td></tr>
            ) : filtered.map((s, i) => {
              const b = parseFloat(s.basicSalary) || 0;
              const net = b + b*(parseFloat(s.daPercent)||0)/100 + b*(parseFloat(s.hraPercent)||0)/100 - b*(parseFloat(s.pfPercent)||0)/100;
              const avatarColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
              return (
                <tr key={s.id} style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: 10, background: avatarColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 800, fontSize: 13, flexShrink: 0,
                      }}>{initials(s.name)}</div>
                      <div>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>{s.name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{s.employeeCode}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ fontSize: 12, color: '#475569' }}>{s.phone || '-'}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{s.email || '-'}</div>
                  </td>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ color: '#475569', fontSize: 12 }}>{s.department?.name || '-'}</div>
                    <div style={{ color: '#94a3b8', fontSize: 11 }}>{s.designation || '-'}</div>
                  </td>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ fontWeight: 700, color: '#10b981', fontSize: 14 }}>₹{net.toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 10, color: '#94a3b8' }}>Basic: ₹{b.toLocaleString('en-IN')}</div>
                  </td>
                  <td style={{ padding: '13px 20px' }}>
                    <span style={{
                      padding: '4px 14px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                      background: s.isActive !== false ? '#d1fae5' : '#fee2e2',
                      color:      s.isActive !== false ? '#065f46' : '#991b1b',
                    }}>{s.isActive !== false ? 'ACTIVE' : 'INACTIVE'}</span>
                  </td>
                  <td style={{ padding: '13px 20px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => navigate(`/staff/${s.id}/kundali`)} style={{
                        padding: '6px 14px', borderRadius: 8, background: '#ede9fe',
                        color: '#7c3aed', border: '1px solid #c4b5fd', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600,
                      }}>Full Info</button>
                      <button onClick={() => openEdit(s)} style={{
                        padding: '6px 14px', borderRadius: 8, background: '#eff6ff',
                        color: '#2563eb', border: '1px solid #bfdbfe', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600,
                      }}>Edit</button>
                      <button onClick={() => handleDelete(s.id)} style={{
                        padding: '6px 14px', borderRadius: 8, background: '#fff1f2',
                        color: '#e11d48', border: '1px solid #fecdd3', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600,
                      }}>Delete</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🔍</div>
            <div style={{ fontWeight: 600 }}>No staff found</div>
          </div>
        )}
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
          <div style={{ background: '#fff', borderRadius: 20, width: '95%', maxWidth: 700, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}>
            {/* Modal Header */}
            <div style={{ background: 'linear-gradient(135deg, #0a1628, #1a237e)', padding: '24px 28px', borderRadius: '20px 20px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontWeight: 800, fontSize: 18 }}>{editing ? 'Edit Staff Member' : 'Add New Staff'}</div>
                <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginTop: 3 }}>{editing ? `Editing: ${editing.name}` : 'Fill in the details below'}</div>
              </div>
              <button onClick={() => setModal(false)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 36, height: 36, borderRadius: '50%', cursor: 'pointer', fontSize: 18 }}>✕</button>
            </div>

            <form onSubmit={handleSubmit} style={{ padding: 28 }}>
              {/* Personal Info */}
              <div style={{ fontSize: 12, fontWeight: 700, color: '#3b82f6', letterSpacing: 1, marginBottom: 16, textTransform: 'uppercase' }}>Personal Information</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 20px' }}>
                <Field label="Full Name *">
                  <input value={form.name} onChange={e => f('name', e.target.value)} required placeholder="e.g. Rahul Sharma" style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Employee Code *">
                  <input value={form.employeeCode} onChange={e => f('employeeCode', e.target.value)} required placeholder="e.g. EMP001" style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Designation">
                  <input value={form.designation} onChange={e => f('designation', e.target.value)} placeholder="e.g. Software Engineer" style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Phone">
                  <input value={form.phone} onChange={e => f('phone', e.target.value)} placeholder="e.g. 9876543210" style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Email">
                  <input type="email" value={form.email} onChange={e => f('email', e.target.value)} placeholder="email@company.com" style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Join Date">
                  <input type="date" value={form.joinDate} onChange={e => f('joinDate', e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = '#3b82f6'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Date of Birth 🎂">
                  <input type="date" value={form.dateOfBirth} onChange={e => f('dateOfBirth', e.target.value)} style={inputStyle} onFocus={e => e.target.style.borderColor = '#ec4899'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                </Field>
                <Field label="Department">
                  <select value={form.departmentId} onChange={e => f('departmentId', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="">— Select Department —</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={e => f('status', e.target.value)} style={{ ...inputStyle, cursor: 'pointer' }}>
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                  </select>
                </Field>
              </div>

              {/* Salary */}
              <div style={{ background: '#f8fafc', borderRadius: 14, padding: '18px 20px', marginTop: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#10b981', letterSpacing: 1, marginBottom: 16, textTransform: 'uppercase' }}>💰 Salary Structure</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 16px' }}>
                  {[
                    { label: 'Basic Salary (₹) *', field: 'basicSalary', placeholder: '25000', required: true },
                    { label: 'DA %', field: 'daPercent', placeholder: '17' },
                    { label: 'HRA %', field: 'hraPercent', placeholder: '10' },
                    { label: 'PF %', field: 'pfPercent', placeholder: '12' },
                  ].map(inp => (
                    <Field key={inp.field} label={inp.label}>
                      <input type="number" value={form[inp.field]} onChange={e => f(inp.field, e.target.value)} placeholder={inp.placeholder} required={!!inp.required} min="0" style={inputStyle} onFocus={e => e.target.style.borderColor = '#10b981'} onBlur={e => e.target.style.borderColor = '#e2e8f0'} />
                    </Field>
                  ))}
                </div>
                {form.basicSalary && (
                  <div style={{ background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
                    {(() => {
                      const b = parseFloat(form.basicSalary) || 0;
                      const da = b * (parseFloat(form.daPercent) || 0) / 100;
                      const hra = b * (parseFloat(form.hraPercent) || 0) / 100;
                      const pf = b * (parseFloat(form.pfPercent) || 0) / 100;
                      const gross = b + da + hra;
                      const net = gross - pf;
                      return (
                        <>
                          <div style={{ fontSize: 12, color: '#065f46' }}>
                            Basic: ₹{b.toLocaleString('en-IN')} + DA: ₹{da.toLocaleString('en-IN')} + HRA: ₹{hra.toLocaleString('en-IN')} − PF: ₹{pf.toLocaleString('en-IN')}
                          </div>
                          <div style={{ fontWeight: 800, color: '#065f46', fontSize: 15 }}>Net: ₹{net.toLocaleString('en-IN')}</div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setModal(false)} style={{ padding: '11px 24px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13, color: '#475569' }}>Cancel</button>
                <button type="submit" style={{ padding: '11px 32px', borderRadius: 10, background: 'linear-gradient(135deg, #1a237e, #1565c0)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 13, boxShadow: '0 4px 14px rgba(26,35,126,0.3)' }}>
                  {editing ? 'Update Staff' : 'Add Staff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
